/**
 * Déménagement d'un écran (AC-T5, SRF-TRV-056) : l'ancienne adresse redirige vers la nouvelle
 * et l'écran d'arrivée dit UNE FOIS « Cet écran a déménagé ». Mémoire par ancienne adresse,
 * `localStorage` sous try/catch (un stockage indisponible montre l'avis à chaque passage).
 */
import { useEffect, useState, type JSX } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { X } from 'lucide-react'

import { useT } from '@/i18n/language'
import { ecrireContexteUrl, lireContexteUrl } from '@/lib/contexteUrl'

const CLE = 'doit-demenagements-vus'

interface EtatDemenagement { demenage?: string }

function vus(): string[] {
  try {
    const v: unknown = JSON.parse(localStorage.getItem(CLE) ?? '[]')
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []
  } catch { return [] }
}

function marquerVu(ancienne: string): void {
  try { localStorage.setItem(CLE, JSON.stringify([...new Set([...vus(), ancienne])])) } catch { /* ignoré */ }
}

/**
 * Redirige vers `vers` en gardant l'ancienne adresse pour l'avis, et son CONTEXTE de travail (`?programme=`,
 * `?projet=`, `?perspective=`, cf. `lib/contexteUrl`) comme `RedirectionAlias` : sans lui, la barre de contexte
 * complétait l'adresse d'arrivée avec le projet mémorisé — ou celui de l'adresse, selon l'ordre d'arrivée des
 * listes (N-38). Le reste de l'ancienne requête (`?section=`) ne voyage pas.
 */
export function Demenagement({ vers }: { vers: string }): JSX.Element {
  const { pathname, search } = useLocation()
  const etat: EtatDemenagement = { demenage: `${pathname}${search}` }
  const debutRequete = vers.indexOf('?')
  const chemin = debutRequete < 0 ? vers : vers.slice(0, debutRequete)
  const requete = debutRequete < 0 ? '' : vers.slice(debutRequete)
  return <Navigate to={chemin + ecrireContexteUrl(requete, lireContexteUrl(search))} replace state={etat} />
}

/** Avis monté par la coquille, au-dessus du contenu. */
export function AvisDemenagement(): JSX.Element | null {
  const { t } = useT()
  const location = useLocation()
  const ancienne = (location.state as EtatDemenagement | null)?.demenage
  const [affiche, setAffiche] = useState<string | null>(null)
  useEffect(() => {
    if (!ancienne || vus().includes(ancienne)) return
    setAffiche(ancienne)
    marquerVu(ancienne)
  }, [ancienne])
  if (!affiche || affiche !== ancienne) return null
  return (
    <div role="status" data-testid="avis-demenagement"
         className="mb-4 flex items-start gap-2 rounded-md border bg-muted/40 px-3 py-2 text-sm">
      <p className="flex-1">
        {t('demenagement.avis', 'Cet écran a déménagé : l’adresse {{ancienne}} mène désormais ici. Mettez vos signets à jour.', { ancienne: affiche })}
      </p>
      <button type="button" onClick={() => setAffiche(null)} data-testid="avis-demenagement-fermer"
              aria-label={t('demenagement.fermer', 'Fermer l’avis de déménagement')}
              className="flex size-10 shrink-0 items-center justify-center rounded-md hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        <X className="size-4" aria-hidden="true" />
      </button>
    </div>
  )
}
